import { Component, computed, inject } from '@angular/core';
import { getEmployeeEntities } from '@app/reducers/orm/employee/employee.selectors';
import { ScheduleModel } from '@app/reducers/orm/schedule/schedule.model';
import { getEnhancedScheduleEntities } from '@app/reducers/orm/schedule/schedule.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconUserAddRegular } from '@sb/mgc-icons/mgcIcon-user_add_regular.icon';
import { ButtonComponent } from '@sb/ui/button';
import { DIALOG_DATA, DialogRef, SbDialogModule } from '@sb/ui/dialog';
import { IconComponent, IconRegistry } from '@sb/ui/icon';

import { FindCoverShiftRowComponent } from '../shift-row/find-cover-shift-row.component';

/**
 * Opened by FindCoverService once an absence is saved — the recommendation is offered
 * here rather than started, since the planner came to record an absence, not to ask
 * for cover.
 *
 * Closes with the occurrence ids to walk: every shift, or the single one the planner
 * picked off the list. Dismissing closes with nothing.
 */
export interface FindCoverPromptData {
  occurrenceIds: string[];
}

@Component({
  templateUrl: './find-cover-prompt.component.html',
  imports: [SbDialogModule, TranslatePipe, ButtonComponent, IconComponent, FindCoverShiftRowComponent],
})
export class FindCoverPromptComponent {
  #data = inject<FindCoverPromptData>(DIALOG_DATA);
  #dialogRef = inject<DialogRef<string[]>>(DialogRef);
  #store = inject<Store<AppState>>(Store);

  #scheduleEntities = this.#store.selectSignal(getEnhancedScheduleEntities);
  #employeeEntities = this.#store.selectSignal(getEmployeeEntities);

  shifts = computed<ScheduleModel[]>(() => {
    const entities = this.#scheduleEntities();

    // A shift missing from the store cannot be named or covered, so it is dropped
    // rather than listed blank — and the count below has to agree with the list.
    return this.#data.occurrenceIds
      .map((occurrenceId) => entities[occurrenceId])
      .filter((shift): shift is ScheduleModel => !!shift);
  });

  isSingleShift = computed(() => this.shifts().length === 1);

  employeeName = computed(() => {
    const [firstShift] = this.shifts();

    return firstShift ? this.#employeeEntities()[firstShift.user_id]?.name : undefined;
  });

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconUserAddRegular]);
  }

  dismiss(): void {
    this.#dialogRef.close();
  }

  /** Only the shifts on show: one dropped for being absent from the store cannot be covered. */
  coverAll(): void {
    this.#dialogRef.close(this.shifts().map((shift) => shift.occurrence_id));
  }

  coverOne(occurrenceId: string): void {
    this.#dialogRef.close([occurrenceId]);
  }
}
